if (!customElements.get('vf-cut-selector')) {
  class VfCutSelector extends HTMLElement {
    connectedCallback() {
      this.defaultCutAllowance = Number(this.dataset.cutAllowance || 0.1);
      this.materialForm = this.dataset.materialForm || 'flat';
      this.roundMinRemnant = Number(this.dataset.roundMinRemnant || 0.5);
      this.priceMode = this.dataset.priceMode || 'full_billet';
      this.checkoutEnabled = this.dataset.checkoutEnabled === 'true';
      this.currency = this.dataset.currency || 'USD';
      this.money = new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency });
      this.variants = JSON.parse(this.querySelector('[data-vf-cut-variants]').textContent).map((variant) => this.normalizeVariant(variant));
      this.alloysEl = this.querySelector('[data-vf-alloys]');
      this.billetsEl = this.querySelector('[data-vf-billets]');
      this.lengthInput = this.querySelector('[data-vf-length]');
      this.fullInput = this.querySelector('[data-vf-full]');
      this.validationEl = this.querySelector('[data-vf-validation]');
      this.mathEl = this.querySelector('[data-vf-math]');
      this.totalEl = this.querySelector('[data-vf-total]');
      this.rateEl = this.querySelector('[data-vf-rate]');
      this.submit = this.querySelector('[data-vf-submit]');
      this.form = this.querySelector('[data-vf-form]');
      this.controlsEl = this.querySelector('[data-vf-controls]');
      this.soldOutEl = this.querySelector('[data-vf-sold-out]');
      this.dpoReady = false;
      this.currentTotalCents = 0;
      this.syncedVariantId = null;
      const availableVariants = this.variants.filter((variant) => variant.available);

      if (!availableVariants.length) {
        this.controlsEl.hidden = true;
        this.soldOutEl.hidden = false;
        this.setAttribute('data-sold-out', 'true');
        this.setAttribute('data-ready', 'true');
        return;
      }

      const requestedId = new URL(window.location.href).searchParams.get('variant');
      const preferred = availableVariants.find((variant) => String(variant.id) === requestedId)
        || availableVariants.find((variant) => /stainless/i.test(variant.alloy))
        || availableVariants[0];
      this.activeAlloy = preferred.alloy;
      this.activeVariant = preferred;
      this.bindEvents();
      this.bindDpo();
      this.bindSubmitObserver();
      this.renderAlloys();
      this.renderBillets();
      this.resetLength();
      this.setAttribute('data-ready', 'true');
    }

    disconnectedCallback() {
      this.submitObserver?.disconnect();
    }

    normalizeVariant(variant) {
      const parsed = this.parseDimensions(variant.dimensions || variant.title);
      const availableLength = Number(variant.availableLength);
const physicalLength = Number.isFinite(availableLength) ? availableLength : parsed.length || 0;
      const materialForm = variant.materialForm || this.materialForm;
      const pricePerInch = Number(variant.pricePerInch) > 0
        ? Number(variant.pricePerInch) * 100
        : physicalLength > 0 ? Number(variant.priceCents) / physicalLength : 0;
      const fullBilletPrice = pricePerInch > 0 && physicalLength > 0
  ? pricePerInch * physicalLength
  : Number(variant.fullBilletPrice) > 0
    ? Number(variant.fullBilletPrice) * 100
    : Number(variant.priceCents) || 0;
      const cutAllowance = Number(variant.cutAllowance) > 0
        ? Number(variant.cutAllowance)
        : this.defaultCutAllowance;
      // Flat billets may leave any remainder; keep the existing round-stock rule.
      const minimumRemnant = materialForm === 'round'
        ? Number(variant.minimumRemnant) > 0 ? Number(variant.minimumRemnant) : this.roundMinRemnant
        : 0;
      const sellingIncrement = Number(variant.sellingIncrement) > 0 ? Number(variant.sellingIncrement) : 1;
      const cutEnabled = variant.cutEnabled === null || variant.cutEnabled === undefined
        ? true
        : Boolean(variant.cutEnabled);
      return {
        ...variant,
        alloy: variant.alloy || 'Standard',
        materialForm,
        length: physicalLength,
        thickness: Number(variant.thickness) || parsed.thickness,
        width: Number(variant.width) || parsed.width,
        diameter: Number(variant.diameter) || parsed.diameter,
        pricePerInch,
        fullBilletPrice,
        cutAllowance,
        minimumRemnant,
        sellingIncrement,
        available: Boolean(variant.available) && cutEnabled && physicalLength > 0,
      };
    }

    parseDimensions(value) {
      const numbers = String(value || '').match(/(?:\d+\.?\d*|\.\d+)/g)?.map(Number) || [];
      if (this.materialForm === 'round') return { length: numbers[0] || 0, diameter: numbers[1] || 0, width: 0, thickness: 0 };
      return { length: numbers[0] || 0, width: numbers[1] || 0, thickness: numbers[2] || 0, diameter: 0 };
    }

    bindEvents() {
      this.querySelector('[data-vf-minus]').addEventListener('click', () => {
        if (this.requiresFullBillet()) return this.update();
        this.fullInput.checked = false;
        this.lengthInput.disabled = false;
        this.lengthInput.value = Math.max(this.activeVariant.sellingIncrement, Number(this.lengthInput.value || this.activeVariant.sellingIncrement) - this.activeVariant.sellingIncrement);
        this.update();
      });
      this.querySelector('[data-vf-plus]').addEventListener('click', () => {
        if (this.requiresFullBillet()) return this.update();
        this.fullInput.checked = false;
        this.lengthInput.disabled = false;
        this.lengthInput.value = Number(this.lengthInput.value || 0) + this.activeVariant.sellingIncrement;
        this.update();
      });
      this.lengthInput.addEventListener('input', () => {
        if (this.requiresFullBillet()) return this.update();
        this.fullInput.checked = false;
        this.lengthInput.disabled = false;
        this.update();
      });
      this.fullInput.addEventListener('change', () => {
        this.lengthInput.disabled = this.fullInput.checked;
        this.update();
      });
      this.form.addEventListener('submit', (event) => {
        if (!this.isValid() || !this.checkoutEnabled) event.preventDefault();
      });
    }

    bindDpo() {
      const initialize = () => {
        this.dpoReady = Boolean(document.querySelector('#itoris_dynamicproductoptions .pr input'));
        this.update();
        return this.dpoReady;
      };

      const poll = (attempt = 0) => {
        if (initialize() || attempt >= 40) return;
        window.setTimeout(() => poll(attempt + 1), 250);
      };

      if (window.jQuery) {
        window.jQuery(window).on('dpo_initialized.vfCutSelector', initialize);
      }

      poll();
    }

    bindSubmitObserver() {
      this.submitObserver = new MutationObserver((mutations) => {
        const submitAdded = mutations.some((mutation) => [...mutation.addedNodes].some((node) => (
          node.nodeType === Node.ELEMENT_NODE
          && (node.matches?.('[data-vf-submit]') || node.querySelector?.('[data-vf-submit]'))
        )));
        if (submitAdded) this.update();
      });
      this.submitObserver.observe(this.form, { childList: true, subtree: true });
    }

    renderAlloys() {
      const alloys = [...new Set(this.variants.filter((variant) => variant.available).map((variant) => variant.alloy))].sort((a, b) => /stainless/i.test(a) ? -1 : /stainless/i.test(b) ? 1 : a.localeCompare(b));
      this.alloysEl.innerHTML = alloys.map((alloy) => {
        const group = this.variants.filter((variant) => variant.alloy === alloy);
        const count = group.filter((variant) => variant.available).length;
        return `<button type="button" class="vf-cut__alloy ${alloy === this.activeAlloy ? 'is-selected' : ''}" data-alloy="${this.escape(alloy)}" ${count ? '' : 'disabled'}><strong>${this.escape(alloy)}</strong><small>${count ? `${count} billet${count === 1 ? '' : 's'} available` : 'Out of stock'}</small></button>`;
      }).join('');
      this.alloysEl.querySelectorAll('[data-alloy]').forEach((button) => button.addEventListener('click', () => {
        this.activeAlloy = button.dataset.alloy;
        this.activeVariant = this.variants.find((variant) => variant.available && variant.alloy === this.activeAlloy);
        this.renderAlloys();
        this.renderBillets();
        this.resetLength();
      }));
    }

    renderBillets() {
      const group = this.variants.filter((variant) => variant.alloy === this.activeAlloy);
      this.billetsEl.innerHTML = group.map((variant) => {
        const selected = variant.id === this.activeVariant?.id;
        const size = variant.materialForm === 'round' || variant.materialForm === 'round_bar'
          ? `${this.dimension(variant.diameter)}″ OD`
          : `${this.dimension(variant.thickness, true)}″ × ${this.dimension(variant.width)}″`;
        return `<button type="button" class="vf-cut__billet ${selected ? 'is-selected' : ''}" data-variant-id="${variant.id}" ${variant.available ? '' : 'disabled'}><span class="vf-cut__radio"></span><span class="vf-cut__billet-size"><strong>${size}</strong><small>${this.length(variant.length)}″ available · SKU ${this.escape(variant.sku || '—')}</small>${variant.coreMaterial ? `<small>Core: ${this.escape(variant.coreMaterial)}</small>` : ''}</span><span class="vf-cut__billet-price"><strong>${this.money.format(variant.pricePerInch / 100)}</strong><small>per inch</small></span></button>`;
      }).join('');
      this.billetsEl.querySelectorAll('[data-variant-id]').forEach((button) => button.addEventListener('click', () => {
        this.activeVariant = this.variants.find((variant) => String(variant.id) === button.dataset.variantId);
        this.renderBillets();
        this.resetLength();
      }));
    }

    resetLength() {
      if (!this.activeVariant) return;
      const maxPartial = this.maximumPartialLength();
      this.fullInput.checked = this.requiresFullBillet() || maxPartial < this.activeVariant.sellingIncrement;
      this.lengthInput.disabled = this.fullInput.checked;
      this.lengthInput.min = this.activeVariant.sellingIncrement;
      this.lengthInput.step = this.activeVariant.sellingIncrement;
      this.lengthInput.value = this.fullInput.checked ? this.activeVariant.length : Math.min(8, maxPartial);
      this.update();
    }

    requiresFullBillet() {
      return this.activeVariant.length <= 7;
    }

    maximumPartialLength() {
      if (this.requiresFullBillet()) return 0;
      const variant = this.activeVariant;
      const available = variant.length - variant.cutAllowance - variant.minimumRemnant;
      return Math.max(0, Math.floor((available + 1e-9) / variant.sellingIncrement) * variant.sellingIncrement);
    }

    result() {
      const full = this.requiresFullBillet() || this.fullInput.checked;
      const requested = full ? this.activeVariant.length : Number(this.lengthInput.value);
      const remaining = full ? 0 : this.activeVariant.length - requested - this.activeVariant.cutAllowance;
      const units = requested / this.activeVariant.sellingIncrement;
      const validIncrement = full || Math.abs(units - Math.round(units)) < 1e-9;
      const validRange = requested > 0 && requested <= this.activeVariant.length;
      const validRemnant = full || remaining + 1e-9 >= this.activeVariant.minimumRemnant;
      return { full, requested, remaining: Math.max(0, remaining), validIncrement, validRange, validRemnant, valid: validIncrement && validRange && validRemnant };
    }

    isValid() { return Boolean(this.activeVariant && this.result().valid); }

    update() {
      if (!this.activeVariant) return;
      const fullRequired = this.requiresFullBillet();
      if (fullRequired) {
        this.fullInput.checked = true;
        this.lengthInput.value = this.activeVariant.length;
      }
      this.querySelector('[data-vf-full-notice]').hidden = !fullRequired;
      this.fullInput.disabled = fullRequired;
      this.lengthInput.disabled = this.fullInput.checked;
      this.querySelector('[data-vf-minus]').disabled = fullRequired;
      this.querySelector('[data-vf-plus]').disabled = fullRequired;
      const result = this.result();
      const variant = this.activeVariant;
      this.querySelector('[data-vf-full-copy]').textContent = `Get the entire ${this.length(this.activeVariant.length)}″ billet`;
      this.querySelector('[data-vf-rule-copy]').textContent = fullRequired
        ? this.dataset.fullRequiredMessage
        : `Sold in ${this.length(variant.sellingIncrement)}″ increments.`;
      if (!result.valid) {
        this.validationEl.hidden = false;
        if (!result.validIncrement) this.validationEl.innerHTML = `<strong>Please enter a valid length.</strong>This material is sold in ${this.length(variant.sellingIncrement)}″ increments.`;
        else if (!result.validRange) this.validationEl.innerHTML = `<strong>That length is not available.</strong>This billet currently has ${this.length(this.activeVariant.length)}″ available.`;
        else this.validationEl.textContent = this.dataset.cutUnavailableMessage;
      } else this.validationEl.hidden = true;
      this.mathEl.hidden = true;
      this.mathEl.innerHTML = '';
      const totalCents = Math.round(result.full ? variant.fullBilletPrice : result.requested * variant.pricePerInch);
      this.currentTotalCents = totalCents;
      this.totalEl.textContent = this.money.format(totalCents / 100);
      this.rateEl.textContent = result.full
        ? `Full ${this.length(variant.length)}″ billet`
        : `${this.money.format(variant.pricePerInch / 100)} × ${this.length(result.requested)}″`;
      this.querySelector('[data-vf-variant-id]').value = this.activeVariant.id;
      this.querySelector('[data-vf-quantity]').value = 1;
      this.querySelector('[data-vf-property-sku]').value = this.activeVariant.sku || '';
      this.querySelector('[data-vf-property-cut-type]').value = result.full ? 'Full billet' : 'Partial cut';
      this.querySelector('[data-vf-property-length]').value = `${this.length(result.requested)} inches`;
      this.querySelector('[data-vf-property-available]').value = `${this.length(this.activeVariant.length)} inches`;
      this.querySelector('[data-vf-property-allowance]').value = result.full ? '0 inches' : `${this.length(variant.cutAllowance)} inch`;
      this.querySelector('[data-vf-property-remaining]').value = `${this.length(result.remaining)} inches`;
      this.syncShopifyVariant();
      this.syncDpoPrice();
      const checkoutReady = this.checkoutEnabled || this.dpoReady;
      this.querySelectorAll('[data-vf-submit]').forEach((button) => {
        button.disabled = !result.valid || !checkoutReady;
        button.textContent = checkoutReady ? (result.full ? window.variantStrings.addToCart.toUpperCase() : 'ADD CUT TO CART') : 'LOADING CHECKOUT…';
      });
      this.querySelector('[data-vf-note]').textContent = 'Your price is based only on the length you order.';
    }

    syncShopifyVariant() {
      const variantId = String(this.activeVariant.id);
      const inputs = document.querySelectorAll('[name="id"]');
      let changed = this.syncedVariantId !== variantId;
      inputs.forEach((input) => {
        if (input.value !== variantId) changed = true;
        input.value = variantId;
      });

      if (!changed) return;
      this.syncedVariantId = variantId;

      if (window.jQuery) {
        window.jQuery(inputs).trigger('change');
      } else {
        inputs.forEach((input) => input.dispatchEvent(new Event('change', { bubbles: true })));
      }

      const finalize = () => {
        document.querySelectorAll('[name="id"]').forEach((input) => { input.value = variantId; });
        this.syncDpoPrice();
      };
      window.setTimeout(finalize, 0);
      window.setTimeout(finalize, 150);
    }

    syncDpoPrice() {
      if (!window.jQuery || !this.isValid()) return false;
      const priceInput = window.jQuery('#itoris_dynamicproductoptions .pr input');
      if (!priceInput.length) return false;
      const price = (this.currentTotalCents / 100).toFixed(2);
      if (priceInput.val() !== price) priceInput.val(price).trigger('change');
      this.dpoReady = true;
      return true;
    }

    length(value) { return Number(value || 0).toFixed(3).replace(/0+$/, '').replace(/\.$/, ''); }
    dimension(value, keepThree = false) { const number = Number(value || 0); return keepThree ? number.toFixed(3).replace(/^0/, '') : this.length(number).replace(/^0/, ''); }
    escape(value) { const div = document.createElement('div'); div.textContent = String(value ?? ''); return div.innerHTML; }
  }

  customElements.define('vf-cut-selector', VfCutSelector);
}

